import { AlertTriangle, CheckCircle2, Info } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

export type CompletenessTone = 'ok' | 'warning' | 'critical' | 'info'

interface DataCompletenessBadgeProps {
  /** Coverage percentage 0-100. Drives tone if `tone` is omitted. */
  coverage?: number | null
  /** Override the auto-derived tone. */
  tone?: CompletenessTone
  /** Short label rendered inside the pill (defaults to "X% covered"). */
  label?: string
  /** Tooltip body explaining the gap and how to close it. */
  detail: string
  className?: string
}

/**
 * A small inline badge that flags how complete the underlying data is
 * for a derived metric (e.g. "Net Profit" depends on COGS coverage —
 * if only 40% of items have a cost set, the user must know).
 */
export function DataCompletenessBadge({
  coverage,
  tone,
  label,
  detail,
  className,
}: DataCompletenessBadgeProps) {
  const resolvedTone: CompletenessTone =
    tone ?? deriveTone(coverage ?? 0)
  const display = label ?? defaultLabel(coverage)

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold cursor-help',
              TONE_STYLES[resolvedTone],
              className,
            )}
          >
            <ToneIcon tone={resolvedTone} />
            {display}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
          {detail}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function deriveTone(coverage: number): CompletenessTone {
  if (coverage >= 80) return 'ok'
  if (coverage >= 50) return 'warning'
  return 'critical'
}

function defaultLabel(coverage?: number | null): string {
  if (coverage == null) return 'Data incomplete'
  return `${Math.round(coverage)}% covered`
}

const TONE_STYLES: Record<CompletenessTone, string> = {
  ok: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300',
  warning:
    'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300',
  critical:
    'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300',
  info: 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-300',
}

function ToneIcon({ tone }: { tone: CompletenessTone }) {
  if (tone === 'ok') return <CheckCircle2 className="h-3 w-3" aria-hidden />
  if (tone === 'info') return <Info className="h-3 w-3" aria-hidden />
  return <AlertTriangle className="h-3 w-3" aria-hidden />
}
